import React, { BaseSyntheticEvent, ChangeEvent, useEffect, useMemo, useRef, useState } from "react";

import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { FileUpload, FileUploadUploadEvent } from "primereact/fileupload";
import { Image } from "primereact/image";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { ToggleButton, ToggleButtonChangeEvent } from "primereact/togglebutton";
import { T } from "services/lang/LanguageService";
import useScadaStore from "store/scada-store";
import { ChronoUnitLegacy } from "utils/time-utils";
import { isObjectEmpty } from "utils/utils";
import { v4 as uuidv4 } from "uuid";
import ScadaDatasetsTable from "../components/ScadaDatasetsTable";
import { ImagesUploadResponse, LabelLayer, ScadaEditorProps, ScadaProps } from "../scadaTypes";
import { IMAGE_UPLOAD_URL } from "../utils/const";
import {
  DEFAULT_LAYER_NAME,
  defaultLayer,
  defaultScadaConfig
} from "../utils/default-values";
import { getScadaImageUrl, tempId } from "../utils/scadaUtils";

const ScadaEditor: React.FC<ScadaEditorProps> = ({ config, onConfigChange }) => {
  const [currentConfig, setCurrentConfig] = useState<ScadaProps>(() =>
    isObjectEmpty(config) ? { ...defaultScadaConfig, id: uuidv4() } : config
  );
  const [newLayer, setNewLayer] = useState<LabelLayer>(defaultLayer);
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const scadaId = config.id ?? tempId(config);
  const scada = useScadaStore(state => state.scadaMap.get(scadaId));
  const addScada = useScadaStore(state => state.addScada);
  const updateScadaLabelLayers = useScadaStore(state => state.updateScadaLabelLayers);
  const updateScadaLabels = useScadaStore(state => state.updateScadaLabels);
  
  const scadaLabels = scada?.labels;
  const scadaLabelLayers = scada?.labelLayers;

  useEffect(() => {
    if (isObjectEmpty(config)) {
      onConfigChange(currentConfig);
      addScada({
        scadaId: currentConfig.id!,
        labels: currentConfig.elements!,
        labelLayers: currentConfig.labelLayers!,
      });
      return;
    }
    if (!scada) {
      onConfigChange({ ...config, id: currentConfig.id ?? scadaId });
      addScada({
        scadaId: currentConfig.id ?? scadaId,
        labels: config.elements ?? [],
        labelLayers: config.labelLayers ?? [defaultLayer],
      });
    } else {
      setCurrentConfig({
        ...config,
        elements: scadaLabels ?? [],
        labelLayers: scadaLabelLayers  ?? [defaultLayer],
        id: config.id ?? currentConfig.id,
      });
      onConfigChange({
        ...config,
        elements: scadaLabels ?? [],
        labelLayers: scadaLabelLayers ?? [defaultLayer],
        id: config.id ?? currentConfig.id,
      });
    }
  }, []);

  const scadaBgUrl = getScadaImageUrl(config.scadaBackgroundId);
  const labelLayers = useMemo(() => [...(currentConfig.labelLayers ?? [])], [currentConfig.labelLayers]);

  const handleConfigChange = (
    e:
      | ChangeEvent<HTMLInputElement>
      | BaseSyntheticEvent
      | DropdownChangeEvent
      | CheckboxChangeEvent
  ) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

    const newConfig = {
      ...currentConfig,
      [e.target.name]: value,
    };

    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleLabelLayerChange = (e: ToggleButtonChangeEvent, layer: string) => {
    const updLayer = labelLayers.map(lg => (lg.layer === layer ? { ...lg, visible: e.value } : lg));
    updateScadaLabelLayers(currentConfig.id!, updLayer);
    setCurrentConfig({ ...currentConfig, labelLayers: updLayer });
    onConfigChange({ ...currentConfig, labelLayers: updLayer });
  };

  const addLabelLayer = () => {
    if (labelLayers.find(lg => lg.layer === newLayer.layer)) return;
    const updLayer = [...labelLayers, newLayer];
    updateScadaLabelLayers(currentConfig.id!, updLayer);
    setCurrentConfig({ ...currentConfig, labelLayers: updLayer });
    onConfigChange({ ...currentConfig, labelLayers: updLayer });
    setNewLayer(defaultLayer);
  };

  const removeLabelLayer = (index: number) => {
    const removedLayer = labelLayers.splice(index, 1);
    const updatedElements = currentConfig.elements.map(el =>
      el.layer === removedLayer[0].layer ? { ...el, layer: DEFAULT_LAYER_NAME } : el
    );
    updateScadaLabels(currentConfig.id!, updatedElements);
    updateScadaLabelLayers(currentConfig.id!, labelLayers);
    setCurrentConfig({ ...currentConfig, labelLayers, elements: updatedElements });
    onConfigChange({ ...currentConfig, labelLayers, elements: updatedElements });
  };

  const onUpload = (e: FileUploadUploadEvent) => {
    const response: ImagesUploadResponse = JSON.parse(e.xhr.response);

    const newConfig = {
      ...currentConfig,
      scadaBackgroundId: response.id,
    };
    onConfigChange(newConfig);
    setCurrentConfig(newConfig);
    easasAlert({
      summary: T("alert.success"),
      severity: "success",
      detail: T("alert.image-uploaded"),
    });
  };

  const handleDownload = () => {
    downloadRef.current?.click();
  };

  const mappedLabelLayers = [...labelLayers].map((lg, index) => (
    <div className="p-inputgroup w-8 mb-3" key={lg.layer}>
      <InputText value={lg.layer} disabled />
      <div className="p-inputgroup-addon py-1">
        <ToggleButton
          onLabel={T("word.visible")}
          offLabel={T("word.hidden")}
          checked={lg.visible}
          onChange={e => handleLabelLayerChange(e, lg.layer)}
        />
      </div>
      <Button
        icon="pi pi-times"
        type="button"
        onClick={() => removeLabelLayer(index)}
        className="p-inputgroup-addon p-button-sm p-button-danger"
        disabled={lg.layer === DEFAULT_LAYER_NAME}
      />
    </div>
  ));

  return (
    <div className="grid p-fluid">
      <InputLabelWrapper labelName={T("label.scada.refresh-rate-in-sec")} col={4}>
        <InputNumber
          value={currentConfig.refreshRate}
          name="refreshRate"
          onChange={e => handleConfigChange(e.originalEvent)}
          useGrouping={false}
          max={3600}
          min={0}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scada.not-older-than")} col={4}>
        <div className="p-inputgroup">
          <InputNumber
            value={currentConfig.maxDelayTime}
            name="maxDelayTime"
            onChange={e => handleConfigChange(e.originalEvent)}
            style={{ blockSize: "37px" }}
            className="w-6"
            useGrouping={false}
            max={100}
            min={0}
          />
          <Dropdown
            value={currentConfig.maxDelayTimeUnit}
            options={Object.keys(ChronoUnitLegacy)}
            name="maxDelayTimeUnit"
            onChange={handleConfigChange}
            className="p-inputgroup-addon w-6"
            style={{ blockSize: "37px" }}
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scada.label-truncate-size")} col={4}>
        <InputNumber
          value={currentConfig.truncateSize}
          name="truncateSize"
          onChange={e => handleConfigChange(e.originalEvent)}
          useGrouping={false}
          max={100}
          min={0}
        />
      </InputLabelWrapper>
      <InputLabelWrapper
        labelName={T("label.scada.show-scada-settings-header")}
        col={4}
        flexDirection="row-reverse"
        className="justify-content-end align-items-center gap-3"
      >
        <Checkbox
          checked={currentConfig.showScadaSettings}
          name="showScadaSettings"
          onChange={handleConfigChange}
          className="inline-block"
        />
      </InputLabelWrapper>

      <InputLabelWrapper
        labelName="Use default tags size"
        col={currentConfig.useDefaultTagsSize ? 8 : 4}
        flexDirection="row-reverse"
        className="justify-content-end align-items-center gap-3"
      >
        <Checkbox
          checked={Boolean(currentConfig.useDefaultTagsSize)}
          name="useDefaultTagsSize"
          onChange={handleConfigChange}
          className="inline-block"
        />
      </InputLabelWrapper>
      {!currentConfig.useDefaultTagsSize && (
        <InputLabelWrapper labelName="Tags size (1-12)" col={4}>
          <InputNumber
            value={currentConfig.size}
            name="size"
            onChange={e => handleConfigChange(e.originalEvent)}
            useGrouping={false}
            max={12}
            min={1}
          />
        </InputLabelWrapper>
      )}
      <InputLabelWrapper labelName="" col={4}>
        <FileUpload
          name="file"
          mode="basic"
          url={IMAGE_UPLOAD_URL}
          auto
          chooseOptions={{
            label: T("label.scada.upload-scada-background"),
            icon: "pi pi-upload",
          }}
          onUpload={onUpload}
          accept="image/*"
        />
      </InputLabelWrapper>
      {scadaBgUrl && (
        <InputLabelWrapper
          labelName=""
          col={8}
          className="justify-content-end gap-5"
          flexDirection="row"
        >
          <Image
            src={scadaBgUrl}
            alt={T("alt.scada.scada-background-image")}
            height="35"
            style={{ objectFit: "contain" }}
            preview
          />
          <Button
            onClick={handleDownload}
            type="button"
            label={T("action.download")}
            icon="pi pi-download"
            className="p-button-warning w-12rem"
          />
          <a
            ref={downloadRef}
            style={{ display: "none" }}
            href={`${scadaBgUrl}?download=${Math.random()}`}
            title={T("action.download-image")}
          />
        </InputLabelWrapper>
      )}
      <Accordion className="w-full">
        <AccordionTab header={T("header.scada.label-layers")}>
          {mappedLabelLayers}
          <div className="p-inputgroup w-8">
            <InputText
              placeholder={T("action.add-new-layer")}
              value={newLayer.layer}
              onChange={e =>
                setNewLayer(prev => ({
                  ...prev,
                  layer: e.target.value.trimStart(),
                }))
              }
            />
            <div className="p-inputgroup-addon py-1">
              <ToggleButton
                onLabel={T("word.visible")}
                offLabel={T("word.hidden")}
                checked={newLayer.visible}
                onChange={e => setNewLayer(prev => ({ ...prev, visible: e.value }))}
              />
            </div>
            <Button
              icon="pi pi-check"
              onClick={addLabelLayer}
              type="button"
              className="p-inputgroup-addon p-button-sm p-button-success"
            />
          </div>
        </AccordionTab>
      </Accordion>
      <ScadaDatasetsTable
        currentConfig={currentConfig}
        setCurrentConfig={setCurrentConfig}
        config={config}
        onConfigChange={onConfigChange}
      />
    </div>
  );
};

export default ScadaEditor;
